import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Loader2, CheckCircle2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate, money } from "@/lib/format";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/doc`;
const KIND = { proposal: "Propuesta", invoice: "Factura", quote: "Cotización" };
const CHECKS = [["accept_price", "Acepto el precio de la opción seleccionada"], ["accept_scope", "Acepto el alcance y los entregables"],
  ["accept_renewals", "Acepto las condiciones de renovación"], ["accept_period", "Acepto el periodo contratado"]];
const err = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return "Revise su nombre y correo e intente de nuevo.";
  return typeof d === "string" ? d : "No se pudo completar. Intente de nuevo.";
};

function AcceptBox({ token, info, onDone }) {
  const [f, setF] = useState({ option_key: info.options.length === 1 ? info.options[0].key : "", full_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.option_key) return setMsg("Seleccione una opción.");
    if (f.full_name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(f.email)) return setMsg("Escriba su nombre completo y un correo válido.");
    if (!CHECKS.every(([k]) => f[k])) return setMsg("Debe marcar las cuatro casillas.");
    setBusy(true); setMsg("");
    try { await axios.post(`${BASE}/${token}/accept`, { ...f, version: info.version }); onDone(); }
    catch (e2) { setMsg(err(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-box space-y-3 p-5" data-testid="doc-accept-form">
      <h2 className="font-display text-lg font-bold text-slate-900">Aceptar propuesta</h2>
      <div className="space-y-2">{info.options.map((o) => (
        <label key={o.key} className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border p-3 text-sm ${f.option_key === o.key ? "border-blue-600 bg-blue-50" : ""}`} data-testid={`doc-option-${o.key}`}>
          <span className="flex items-center gap-2"><input type="radio" name="opt" checked={f.option_key === o.key} onChange={() => setF({ ...f, option_key: o.key })} />{o.name}{o.recommended && <b className="text-xs text-orange-600">Recomendada</b>}</span>
          <b>{o.total == null ? "[PENDIENTE]" : money(o.total)}</b>
        </label>))}</div>
      <input className="field-input" placeholder="Nombre completo *" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="doc-accept-name" />
      <input className="field-input" type="email" placeholder="Correo *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="doc-accept-email" />
      {CHECKS.map(([k, l]) => <label key={k} className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-0.5 accent-blue-700" checked={Boolean(f[k])} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`doc-${k}`} />{l}</label>)}
      {msg && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="doc-accept-error">{msg}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="doc-accept-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Aceptar propuesta</Button>
    </form>
  );
}

function PayBox({ token, pay }) {
  const notify = () => { navigator.sendBeacon?.(`${BASE}/${token}/pay-click`) || axios.post(`${BASE}/${token}/pay-click`).catch(() => {}); };
  return (
    <div className="card-box space-y-3 p-5" data-testid="doc-pay-box">
      <h2 className="font-display text-lg font-bold text-slate-900">{pay.installment === "Anticipo" ? "Pagar anticipo" : "Pagar factura"}</h2>
      <p className="text-sm text-slate-600">Factura {pay.invoice_number} · saldo pendiente <b data-testid="doc-pay-amount">{money(pay.amount)}</b></p>
      <Button asChild className="w-full" data-testid="doc-pay-button"><a href={pay.url} target="_blank" rel="noopener noreferrer" onClick={notify}><CreditCard className="mr-2 h-4 w-4" />Pagar con tarjeta</a></Button>
      <p className="text-xs text-slate-500" data-testid="doc-pay-note">Pago seguro con LawPay. Use el número de factura {pay.invoice_number} como referencia. {pay.auto ? "Su pago se registrará en esta factura en unos minutos." : "Al recibirlo, registraremos su pago en esta factura."}</p>
    </div>
  );
}

function PayResult({ sessionId, onPaid }) {
  const [state, setState] = useState("checking");
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); onPaid(); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++n < 10) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  const text = { checking: "Confirmando su pago…", paid: "¡Pago recibido! Gracias.", failed: "El pago no se completó. Puede intentarlo de nuevo.", pending: "Su pago se está procesando. Recargue la página en unos minutos." }[state];
  return <p className={`mt-4 rounded-md px-4 py-3 text-sm ${state === "paid" ? "bg-emerald-50 text-emerald-800" : state === "failed" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-800"}`} data-testid="doc-pay-result">{text}</p>;
}

export default function DocView() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [pages, setPages] = useState(null);
  const [error, setError] = useState("");
  const [n, setN] = useState(0);
  useEffect(() => {
    axios.get(`${BASE}/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(err(e)));
    axios.get(`${BASE}/${token}/preview`).then((r) => setPages(r.data.pages)).catch(() => setPages([]));
  }, [token, n]);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="doc-view-page">
      <header className="border-b bg-white"><div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
        <img src="/logo.png" alt="RinoTECH" className="h-10 w-10" /><span className="font-display text-lg font-extrabold">RinoTECH</span></div></header>
      <main className="mx-auto max-w-5xl px-4 py-6">
        {error ? <p role="alert" className="card-box p-6 text-center text-slate-700" data-testid="doc-view-error">{error}</p>
          : !info ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div> : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">{KIND[info.kind]}</p>
                  <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="doc-view-title">{info.number}{info.title ? ` · ${info.title}` : ""}</h1>
                  <p className="text-sm text-slate-600">{info.client_name} · Estado: <b data-testid="doc-view-status">{info.status}</b> · Enlace válido hasta {fmtDate(info.expires_at.slice(0, 10))}</p>
                </div>
                <a href={`${BASE}/${token}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-md bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800" data-testid="doc-view-download"><Download className="mr-2 h-4 w-4" />Abrir / descargar PDF</a>
              </div>
              {sessionId && <PayResult sessionId={sessionId} onPaid={() => setN((x) => x + 1)} />}
              {params.get("pago") === "cancelado" && <p className="mt-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="doc-pay-cancelled">Pago cancelado. Puede intentarlo de nuevo cuando quiera.</p>}
              {info.acceptance && <p className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="doc-view-accepted"><CheckCircle2 className="h-5 w-5" />Aceptada por {info.acceptance.full_name} el {info.acceptance.accepted_at_local} — {info.acceptance.option_name}</p>}
              <div className={`mt-6 grid gap-6 ${info.can_accept || info.pay ? "lg:grid-cols-[1fr_360px]" : ""}`}>
                <div className="space-y-3" data-testid="doc-view-pages">
                  {pages === null ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
                    : pages.map((src, i) => <img key={i} src={src} alt={`Página ${i + 1}`} className="w-full rounded-md bg-white shadow" data-testid={`doc-view-page-${i + 1}`} />)}
                </div>
                {(info.can_accept || info.pay) && <div className="order-first space-y-4 lg:order-none">{info.can_accept && <AcceptBox token={token} info={info} onDone={() => setN(n + 1)} />}{info.pay && !info.can_accept && <PayBox token={token} pay={info.pay} />}</div>}
              </div>
            </>
          )}
      </main>
    </div>
  );
}
